import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, QrCode, ExternalLink, Trash2, Loader2, Settings, CircleDot, CircleOff, Users } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { listQrcodes, createQrcode, deleteQrcode } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const QRCodeManager = () => {
  const { authed } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => listQrcodes().then(setItems).catch(() => {});

  useEffect(() => {
    if (!authed) return;
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [authed]);

  if (!authed) return null;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Dê um nome ao QR Code.");
    try {
      setCreating(true);
      const created = await createQrcode(name.trim());
      toast.success("QR Code criado! Configure o vídeo e as informações.");
      setOpen(false);
      setName("");
      navigate(`/manage/${created.id}`);
    } catch {
      toast.error("Não foi possível criar o QR Code.");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteQrcode(toDelete.id);
      toast.success("QR Code excluído.");
      setToDelete(null);
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível excluir.";
      toast.error(typeof msg === "string" ? msg : "Não foi possível excluir.");
      setToDelete(null);
    }
  };

  return (
    <motion.section
      data-testid="qrcode-manager"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mt-8 rounded-3xl border border-border bg-card p-6 shadow-sm sm:p-8"
    >
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <QrCode className="h-5 w-5" />
          </span>
          <div>
            <h3 className="font-display text-xl font-semibold">Meus QR Codes</h3>
            <p className="text-sm text-muted-foreground">Crie e gerencie vários QR Codes independentes</p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-qrcode-button" className="rounded-xl">
              <Plus className="mr-2 h-4 w-4" /> Criar outro QR Code
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">Criar outro QR Code</DialogTitle>
              <DialogDescription>
                Dê um nome (ex.: "Culto de Domingo"). Um novo link fixo e QR Code serão gerados,
                totalmente separados dos outros.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 pt-2">
              <div>
                <Label htmlFor="qr-name" className="mb-2 block text-sm">Nome do QR Code</Label>
                <Input
                  id="qr-name" data-testid="new-qrcode-name-input" value={name}
                  onChange={(e) => setName(e.target.value)} placeholder="Ex.: Culto de Domingo"
                  className="h-11 rounded-xl" autoFocus
                />
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="submit-create-qrcode-button" disabled={creating} className="w-full rounded-xl">
                  {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                  Criar e configurar
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3" data-testid="qrcode-list">
        {items.map((it) => (
          <div
            key={it.id}
            data-testid={`qrcode-item-${it.id}`}
            className="flex flex-col gap-3 rounded-2xl border border-border bg-secondary/30 p-4 sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="truncate font-semibold">{it.name}</p>
                {it.is_main && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">Principal</span>
                )}
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${it.published ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                  {it.published ? <CircleDot className="h-3 w-3" /> : <CircleOff className="h-3 w-3" />}
                  {it.published ? "No ar" : "Sem vídeo"}
                </span>
              </div>
              <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">{it.fixed_link}</p>
              <span
                data-testid={`qrcode-today-scans-${it.id}`}
                className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary"
              >
                <Users className="h-3.5 w-3.5" />
                <b className="tabular-nums">{it.today_scans ?? 0}</b> acessos hoje
                <span className="text-primary/50">·</span>
                <span className="text-muted-foreground">{it.total_scans ?? 0} no total</span>
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button asChild variant="ghost" size="sm" data-testid={`qrcode-open-${it.id}`} className="rounded-lg">
                <a href={it.fixed_link} target="_blank" rel="noreferrer"><ExternalLink className="mr-1.5 h-4 w-4" /> Ver</a>
              </Button>
              <Button variant="outline" size="sm" data-testid={`qrcode-manage-${it.id}`} onClick={() => navigate(`/manage/${it.id}`)} className="rounded-lg">
                <Settings className="mr-1.5 h-4 w-4" /> Gerenciar
              </Button>
              {!it.is_main && (
                <Button variant="ghost" size="icon" data-testid={`qrcode-delete-${it.id}`} onClick={() => setToDelete(it)} className="h-9 w-9 rounded-lg text-destructive hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-qrcode-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Excluir "{toDelete?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              O QR Code, o vídeo e as informações deste item serão removidos. Esta ação não pode ser desfeita.
              (O QR Code principal não é afetado.)
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-qrcode-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-qrcode-confirm" onClick={handleDelete}>Sim, excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.section>
  );
};
